import './EventDetailsScreen.css';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import logos from '../../assets/images/loghi/logos.svg';
import redFlag from '../../assets/images/icons/red-flag.svg';
import Accordion from '../_Components/Accordion';
import Modal from '../_Components/Modal';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import Lottie from 'lottie-react';
import hourglass from '../../assets/images/animations/hourglass.json';
import {
  useSessionActions, useLiveUrl, useIsEventStarted
} from '../../context/SessionContext';
import { ApiError } from '../../apis/apis';
import { BackArrowIcon, SoundWaveBlueIcon, YouTubeIcon } from '../../components/Icons';

function EventDetailsScreen() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEventNotStartedModalOpen, setIsEventNotStartedModalOpen] =
    useState(false);
  const [isCheckingEvent, setIsCheckingEvent] = useState(false);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const { checkActiveSession, startSession } = useSessionActions();
  const liveUrl = useLiveUrl();
  const eventHasStarted = useIsEventStarted();

  useEffect(() => {
    const loadSessionData = async () => {
      try {
        await checkActiveSession();
      } catch (error) {
        console.log('Session check failed on mount:', error.message);
      } finally {
        setInitialLoadComplete(true);
      }
    };

    loadSessionData();
  }, [checkActiveSession]);

  const handleStartSession = async () => {
    setIsCheckingEvent(true);

    // Check if event has started before proceeding
    if (eventHasStarted !== true) {
      setIsEventNotStartedModalOpen(true);
      setIsCheckingEvent(false);
      return;
    }

    try {
      const activeSessionData = await checkActiveSession();
      setSessionData(activeSessionData?.data);
      setIsModalOpen(true);
    } catch (error) {
      if (error.message === 'SESSION_COMPLETED') {
        navigate('/end');
        return;
      }

      setSessionData(null);
      setIsModalOpen(true);
    } finally {
      setIsCheckingEvent(false);
    }
  };

  const handleConfirmStartSession = async () => {
    setIsStartingSession(true);
    setIsModalOpen(false);
    setShowLoadingScreen(true);

    try {
      if (sessionData?.session_id) {
        await startSession(sessionData.session_id);
      } else {
        await startSession('new-session');
      }

      navigate('/quiz');
    } catch (error) {
      if (error instanceof ApiError && error.status === 403 && error.details?.recaptchaError) {
        if (error.details.shouldNavigateBack || error.details.userCancelled) {
          setIsStartingSession(false);
          setShowLoadingScreen(false);
        }
        return;
      }

      if (error.message === 'Failed to fetch' || error.message.includes('CORS')) {
        if (window.__showRecaptchaV2Modal) {
          window.__showRecaptchaV2Modal(
            async (v2Token) => {
              try {
                await startSession(sessionData?.session_id || 'new-session');
                setIsStartingSession(false);
                setShowLoadingScreen(false);
                navigate('/quiz');
              } catch (retryError) {
                setIsStartingSession(false);
                setShowLoadingScreen(false);
                alert('ERROR CASE 1: Session start failed after reCAPTCHA retry. Error: ' + retryError.message);
              }
            },
            (error) => {
              setIsStartingSession(false);
              setShowLoadingScreen(false);
              alert('Verifica di sicurezza fallita. Riprova.');
            }
          );
        } else {
          setIsStartingSession(false);
          setShowLoadingScreen(false);
          alert('Errore di configurazione reCAPTCHA. Ricarica la pagina.');
        }
        return;
      }

      setIsStartingSession(false);
      setShowLoadingScreen(false);
      alert('ERROR CASE 2: General session start failure. Error: ' + error.message + ' | Status: ' + (error.status || 'unknown'));
    }
  };


  if (showLoadingScreen) {
    return <LoadingScreen autoNavigate={false} />;
  }

  return (
    <div className='event-detail-container flex flex-col min-h-screen'>
      {initialLoadComplete && eventHasStarted !== null && eventHasStarted !== true && (
        <button
          className='button-ghost mb-6'
          style={{
            marginLeft: -12,
          }}
          onClick={() => navigate('/')}
        >
          <BackArrowIcon />
          Indietro
        </button>
      )}

      <div>
        <img
          alt='Partner logos including MOOD and thefaculty'
          src={logos}
          className='event-logos'
        />

        <h1
          className='w-full text-4xl font-extrabold mt-4'
          style={{
            color: 'var(--text-black-1, #373737)',
            lineHeight: '140%',
          }}
        >
          Obiettivo Medicina, Odontoiatria e Veterinaria
        </h1>
      </div>

      <div className='event-content-section'>
        <ol className='event-detail-list'>
          <li className='event-detail-item flex items-center' data-number='1'>
            <a href={liveUrl} target='_blank' rel='noopener noreferrer' className='event-detail-link'>
              Segui la&nbsp;
              <span style={{ color: '#4674B9', fontWeight: 800, marginRight: '6px', textDecoration: 'underline' }}>
                {' '}
                live
              </span>{' '}

              <SoundWaveBlueIcon />

            </a>
          </li>
          <li className='event-detail-item' data-number='2'>
            Attendi istruzioni del docente per poter iniziare
          </li>
          <li className='event-detail-item' data-number='3'>Rispondi alle domande proposte</li>
          <li className='event-detail-item' data-number='4'>
            Segui la correzione commentata dai docenti
          </li>
        </ol>

        <Accordion
          title='In cosa consiste la sessione?'
          defaultExpanded={false}
          className='event-accordion'
        >
          <div>
            <span className='accordion-content-text highlight'>
              Attendi istruzioni
            </span>
            <span className='accordion-content-text normal'>
              {' '}
              dal docente: inviterà tutti i partecipanti in sala e quelli
              collegati a rispondere ad alcune delle{' '}
            </span>
            <span className='accordion-content-text highlight'>domande</span>
            <span className='accordion-content-text normal'>
              {' '}
              più complesse dell'ultima esercitazione su thefaculty, per poi{' '}
            </span>
            <span className='accordion-content-text highlight'>
              commentare insieme
            </span>
            <span className='accordion-content-text normal'>
              {' '}
              i risultati e fornire alcuni preziosi consigli sulle risposte.
            </span>
          </div>
        </Accordion>
      </div>

      <div className='w-full flex flex-col items-center justify-center mt-auto pb-8 button-section'>
        <button onClick={handleStartSession} disabled={isCheckingEvent || isStartingSession}>
          {isCheckingEvent ? 'Verifica in corso...' : isStartingSession ? 'Avvio sessione...' : 'Inizia la sessione'}
        </button>

        <a href={liveUrl} target='_blank' rel='noopener noreferrer' className='live-button-secondary'>
          <YouTubeIcon />
          Segui la live
          <SoundWaveBlueIcon />
        </a>
      </div>

      {/* Modal for when event is active */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className='modal-icon'>
          <img src={redFlag} alt='Red flag' />
        </div>

        <div className='modal-content-text'>
          <h2 className='modal-title'>Il docente ha dato il via?</h2>
          <p className='modal-subtitle'>
            Prima di continuare, assicurati che il docente abbia comunicato di
            iniziare
          </p>
        </div>

        <div className='modal-buttons'>
          <button
            onClick={handleConfirmStartSession}
            disabled={isStartingSession}
          >
            {isStartingSession ? 'Avvio in corso...' : 'Sì, inizia la sessione'}
          </button>

          <button
            className='button-secondary'
            onClick={() => setIsModalOpen(false)}
          >
            No, attendi
          </button>
        </div>
      </Modal>

      {/* Modal for when event is not started yet */}
      <Modal
        isOpen={isEventNotStartedModalOpen}
        onClose={() => setIsEventNotStartedModalOpen(false)}
      >
        <div className='modal-icon'>
          <Lottie
            animationData={hourglass}
            autoPlay={true}
            loop={true}
            id='loader_animation'
            className='loading-animation'
          />
        </div>

        <div className='modal-content-text'>
          <h2 className='modal-title'>Attendi il via dal docente</h2>
          <p className='modal-subtitle'>
            L'accesso all'evento non è ancora stato attivato, riprova tra
            qualche minuto
          </p>
        </div>

        <div className='modal-buttons'>
          <button onClick={() => setIsEventNotStartedModalOpen(false)}>
            Ho capito
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default EventDetailsScreen;
